import { translate } from '@xdocs/i18n';
import type { Locale, MessageId, TranslateVars } from '@xdocs/i18n';

/**
 * Resolves a DYNAMIC catalog-key string — one that arrived as data (a WS
 * `err` envelope's `UserAlert.message`/`fields[].message`, or an RHF
 * `zodResolver` field error, both of which are catalog keys per E6/E18, not
 * literals) — instead of a call-site literal `t('scope:key')` the
 * `MessageId` union can statically check.
 *
 * OPEN ISSUE (cross-package, not fixable from `apps/web/**`): several
 * catalog keys referenced by already-shipped `@xdocs/schemas`/
 * `@xdocs/ws-protocol` code don't exist in `packages/i18n/src/catalogs/**`
 * yet — e.g. `errors:auth.email.invalid` (packages/schemas/src/auth.dto.ts),
 * `errors:ws.disconnected`/`errors:ws.request_timeout` (packages/
 * ws-protocol/src/correlation.ts). `translate()` THROWS on a missing key
 * outside production (§20.1, by design — catches catalog drift at the call
 * site). A raw server/validation message must never crash the UI it's
 * reporting an error on, so this wrapper catches that throw and falls back
 * to the raw key — ugly but non-fatal — until the `@xdocs/i18n` (F0j) owner
 * backfills the missing keys.
 */
export function safeT(locale: Locale, id: string, vars?: TranslateVars): string {
  try {
    return translate(locale, id as MessageId, vars);
  } catch {
    return id;
  }
}

/**
 * `safeT`, but for an RHF `formState.errors[field]?.message` — which is
 * `string | undefined` (no error vs. a catalog-key string from the shared
 * zod schema's `.message`, E6). Threading the raw key straight into an
 * `<Input error={...}>` would literally render `"errors:auth.email.
 * invalid"` to the user; this resolves it through the same catalog +
 * missing-key fallback as every other dynamic message, and passes through
 * `undefined` untouched so `optionalProp('error', ...)` still omits the
 * prop entirely when the field is valid.
 */
export function safeFieldError(locale: Locale, message: string | undefined): string | undefined {
  return message === undefined ? undefined : safeT(locale, message);
}
